import { CarFront, Layers, Search, Tag, Repeat, BadgeDollarSign, ArrowUpRight } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { scrollToId } from "@/lib/api";
import { PHOTO } from "@/lib/assets";

const SERVICES = [
  {
    icon: CarFront,
    num: "01",
    title: "Achat de véhicule",
    desc: "Recherche personnalisée de véhicules neufs et d'occasion selon vos besoins, votre budget et vos préférences. Nous recherchons, comparons et négocions afin de trouver la meilleure solution possible.",
    target: "#recherche",
    testid: "service-card-achat",
    img: PHOTO.handshake,
  },
  {
    icon: Layers,
    num: "02",
    title: "600+ véhicules d'occasion",
    desc: "Un accès à plus de 600 véhicules d'occasion grâce à notre réseau de garages partenaires : VUS, berlines, camions, véhicules de luxe, sportifs, familiaux, économiques et commerciaux. Trop vaste pour être affiché ici — dites-nous ce que vous cherchez.",
    target: "#inventaire",
    testid: "service-card-inventaire",
  },
  {
    icon: Search,
    num: "03",
    title: "Recherche privée",
    desc: "Vous recherchez un véhicule précis ? Nous le traquons selon la marque, le modèle, l'année, le budget, le kilométrage, l'équipement, la couleur et le type de véhicule souhaités.",
    target: "#recherche",
    testid: "service-card-recherche",
  },
  {
    icon: Tag,
    num: "04",
    title: "Vente de votre véhicule",
    desc: "Évaluation, positionnement du prix, mise en marché, photos, annonce, gestion des demandes et accompagnement jusqu'à la vente — sans les appels, les négociations ni les pertes de temps.",
    target: "#contact",
    testid: "service-card-vente",
  },
  {
    icon: Repeat,
    num: "05",
    title: "Remplacement de véhicule",
    desc: "Remplacez votre véhicule actuel sans gérer les étapes : évaluation, analyse des besoins, recherche, négociation, financement au besoin, coordination et livraison.",
    target: "#contact",
    testid: "service-card-remplacement",
  },
  {
    icon: BadgeDollarSign,
    num: "06",
    title: "Financement automobile",
    desc: "Nouveau service spécialisé : un accompagnement personnalisé pour explorer des solutions de financement adaptées à votre dossier — 1re, 2e et 3e chance au crédit.",
    target: "#financement",
    testid: "service-card-financement",
    badge: "Nouveau",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-anthracite/60">
      <div className="container-x">
        <Reveal>
          <Chapter number="01" label="Nos services" />
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1] tracking-tight max-w-2xl">
              Une expertise complète,
              <br />
              <span className="gold-text">à votre service.</span>
            </h2>
            <p className="text-sm sm:text-base text-slate-400 font-light max-w-md leading-relaxed">
              De l'acquisition à la revente, chaque service est conçu pour vous libérer des démarches.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.num} delay={0.08 * (i % 3)}>
              <article data-testid={s.testid} className="card-lux h-full flex flex-col group !p-0 overflow-hidden">
                {s.img && (
                  <div className="h-44 overflow-hidden">
                    <img
                      src={s.img}
                      alt="Poignée de main naturelle après une transaction automobile réussie"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-8 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-8">
                  <span className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center text-gold group-hover:border-gold/50 group-hover:bg-gold/5 transition-all duration-500">
                    <s.icon size={20} strokeWidth={1.5} />
                  </span>
                  <div className="flex items-center gap-3">
                    {s.badge && (
                      <span className="text-[10px] uppercase tracking-[0.2em] bg-gold/15 text-gold-light border border-gold/30 rounded-full px-3 py-1">
                        {s.badge}
                      </span>
                    )}
                    <span className="font-mono text-xs text-slate-500">{s.num}</span>
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-medium text-white tracking-tight mb-4">{s.title}</h3>
                <p className="text-sm text-slate-400 font-light leading-relaxed flex-1">{s.desc}</p>
                <button
                  data-testid={`${s.testid}-link`}
                  onClick={() => scrollToId(s.target)}
                  className="mt-7 self-start inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold-light hover:text-white transition-colors"
                >
                  En savoir plus <ArrowUpRight size={13} />
                </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
